// Derives the dock's collapsed-tab display state from the same payload that
// drives the bell's "Ad alerts" row. Kept as a pure helper so it can be
// unit-tested without rendering the dock.

import type { AdsActivityResponse } from "@/lib/adsAssistantClient";
import type { AuthContextValue } from "@/contexts/AuthContext";
import type { KpiTone } from "@/components/common/kpi/kpiTypes";
import { resolveAdsHeaderActivityPill } from "@/components/ads-assistant/resolveAdsHeaderActivityPill";
import type { ActivityDockStatusReasonCode } from "./fireflyStatusReason";

export type ActivityDockStatus = {
  /** Tone to render the status dot in. `null` ⇒ no dot, dock stays neutral. */
  tone: KpiTone | null;
  /** Short numeric/label summary (e.g. "2/5", "—") rendered under the label. */
  summary: string;
  /** Aria label for the tab button. */
  ariaLabel: string;
  /**
   * Machine-stable reason the dock is not in ordinary (Focus) state.
   * `null` when calm. Used for diagnostics / telemetry; UI copy uses
   * `reasonLabel`.
   */
  reasonCode: ActivityDockStatusReasonCode | null;
  /** Human-readable why Firefly is alerting / nudging / celebrating. */
  reasonLabel: string | null;
};

const FALLBACK: ActivityDockStatus = {
  tone: null,
  summary: "",
  ariaLabel: "Firefly — open activity panel",
  reasonCode: null,
  reasonLabel: null,
};

/**
 * Roles whose dock urgency reflects the ads-coaching goal. Owners / admins /
 * trainers see the dock so they can review activity, but the pulsing
 * "you missed your ads goal" tone is sales-only — see dev-sync note
 * 6ba20c31-168c-450f-b92e-b21c6ccb4187.
 *
 * Checked via **literal** token membership on `auth.roles` (not
 * `hasRole`), because `roleTokenMatches` lets `platform_owner` /
 * `platform_admin` / `platform_dev` satisfy every org-scoped gate —
 * including `"sales"`. That elevation is correct for access, but it
 * re-armed Firefly's ads-goal alert for platform operators browsing
 * orgs (dev-sync `69ae7791`).
 */
const ADS_URGENCY_AUDIENCE_ROLES = ["sales"] as const;
const PURCHASING_URGENCY_ROLES = ["manufacturing_purchasing", "manufacturing"] as const;
const QC_URGENCY_ROLES = ["mfg_qc", "mfg_final_qc", "owner", "org_owner"] as const;

/**
 * Threshold where the unsent-count pill flips from amber (warning) to red
 * (danger). Mirrors the threshold the retired `LeadsModeBanners` badge row
 * used so the visual urgency cadence sales users are already trained on
 * carries over to the dock-tab badge.
 */
const UNSENT_DANGER_THRESHOLD = 5;

/** True when `auth.roles` literally holds one of `audience` (no platform elevation). */
export function holdsLiteralAudienceRole(
  auth: Pick<AuthContextValue, "roles"> | null | undefined,
  audience: readonly string[],
): boolean {
  if (!auth?.roles?.length) return false;
  const held = new Set(auth.roles.map((r) => String(r).trim().toLowerCase()));
  return audience.some((r) => held.has(String(r).trim().toLowerCase()));
}

export type DeriveActivityDockStatusInput = {
  /** Ads-activity payload from `useAdsHeaderActivity`. Powers the legacy
   * ads-coaching pill that the dock has shown since Phase 6. */
  activity: AdsActivityResponse | null | undefined;
  auth: AuthContextValue | null | undefined;
  /**
   * Slice D — count of unsent leads visible in the current workspace
   * (i.e. `leads.filter(l => !l.sent && !l.isTest).length`). When
   * positive and the user is a sales role, takes priority over the
   * ads-coaching pill so the dock-tab badge surfaces the more urgent
   * workflow signal: a CRM ingestion gap. `0` or `undefined` falls
   * through to the existing ads-coaching behavior.
   */
  unsentCount?: number;
  /** Unacknowledged plant material requests waiting on purchasing. */
  plantRequestReviewCount?: number;
  /** Materials left below zero by a materials-usage submit. */
  negativeOnHandCount?: number;
  /** Unfixed Builder Assistant observations. */
  openQcCount?: number;
  /** Active materials with a stale weighted-average cost. */
  staleWacCount?: number;
  /** Open knowledge-base gap inbox items. */
  kbGapOpenCount?: number;
  /** AI prompting suggestions waiting on an apply role. */
  aiPromptingOpenCount?: number;
  /** Pending org member approvals. */
  pendingMemberApprovalTotal?: number;
  /**
   * When a reason's landing mode is not reachable, or its open callback
   * is missing, that branch is skipped. Omitted keys stay allowed so
   * existing callers keep today's glow.
   */
  landingReady?: Partial<Record<ActivityDockStatusReasonCode, boolean>>;
};

export function deriveActivityDockStatus(
  input: DeriveActivityDockStatusInput,
): ActivityDockStatus {
  const {
    activity,
    auth,
    unsentCount = 0,
    plantRequestReviewCount = 0,
    negativeOnHandCount = 0,
    openQcCount = 0,
    staleWacCount = 0,
    kbGapOpenCount = 0,
    aiPromptingOpenCount = 0,
    pendingMemberApprovalTotal = 0,
    landingReady,
  } = input;
  if (!auth) return FALLBACK;

  const ready = (code: ActivityDockStatusReasonCode) => landingReady?.[code] !== false;
  const isSalesAudience = holdsLiteralAudienceRole(auth, ADS_URGENCY_AUDIENCE_ROLES);
  const isPurchasingAudience = holdsLiteralAudienceRole(auth, PURCHASING_URGENCY_ROLES);
  const isQcAudience = holdsLiteralAudienceRole(auth, QC_URGENCY_ROLES);
  const isMfgDeskAudience = holdsLiteralAudienceRole(auth, ["manufacturing"]);
  const isKbAudience = holdsLiteralAudienceRole(auth, ["trainer", "admin", "owner", "dev"]);
  const isApprovalAudience = holdsLiteralAudienceRole(auth, ["admin", "owner", "org_admin", "org_owner"]);

  // Unsent leads take priority for sales users — a lead sitting locally
  // is an active workflow gap (the lead isn't in GHL yet) which beats
  // the steady-state ads-coaching pill in urgency. Non-sales roles
  // don't see unsent badging because they don't send leads.
  if (isSalesAudience && unsentCount > 0 && ready("unsent_leads")) {
    const tone: KpiTone =
      unsentCount >= UNSENT_DANGER_THRESHOLD ? "red" : "amber";
    const summary = unsentCount > 99 ? "99+" : String(unsentCount);
    const reasonLabel = `${unsentCount} unsent lead${unsentCount === 1 ? "" : "s"} waiting for the CRM`;
    return {
      tone,
      summary,
      ariaLabel: `Firefly — open activity panel — ${reasonLabel}`,
      reasonCode: "unsent_leads",
      reasonLabel,
    };
  }

  if (isQcAudience && openQcCount > 0 && ready("open_qc")) {
    const n = openQcCount;
    const reasonLabel = n === 1 ? "1 open QC item" : `${n} open QC items`;
    return {
      tone: "amber",
      summary: n > 99 ? "99+" : String(n),
      ariaLabel: `Firefly — open activity panel — ${reasonLabel}`,
      reasonCode: "open_qc",
      reasonLabel,
    };
  }

  if (isPurchasingAudience && negativeOnHandCount > 0 && ready("negative_on_hand")) {
    const n = negativeOnHandCount;
    const reasonLabel =
      n === 1
        ? "A material is below zero. Please fix inventory."
        : `${n} materials are below zero. Please fix inventory.`;
    return {
      tone: "amber",
      summary: n > 99 ? "99+" : String(n),
      ariaLabel: `Firefly — open activity panel — ${reasonLabel}`,
      reasonCode: "negative_on_hand",
      reasonLabel,
    };
  }

  if (isPurchasingAudience && plantRequestReviewCount > 0 && ready("plant_material_request")) {
    const n = plantRequestReviewCount;
    const reasonLabel =
      n === 1
        ? "There's a material request. Please review."
        : `There are ${n} material requests. Please review.`;
    return {
      tone: "amber",
      summary: n > 99 ? "99+" : String(n),
      ariaLabel: `Firefly — open activity panel — ${reasonLabel}`,
      reasonCode: "plant_material_request",
      reasonLabel,
    };
  }

  if (activity && isSalesAudience && ready("ads_goal")) {
    const pill = resolveAdsHeaderActivityPill(activity, auth);
    if (pill?.visible && (pill.tone === "amber" || pill.tone === "red")) {
      return {
        tone: pill.tone,
        summary: pill.label.length <= 8 ? pill.label : "",
        ariaLabel: `Firefly — open activity panel — ${pill.ariaLabel}`,
        reasonCode: "ads_goal",
        reasonLabel: pill.ariaLabel.replace(/\.\s*$/, ""),
      };
    }
  }

  if (isMfgDeskAudience && staleWacCount > 0 && ready("stale_wac")) {
    const reasonLabel =
      staleWacCount === 1
        ? "1 material has a stale cost"
        : `${staleWacCount} materials have a stale cost`;
    return {
      tone: "amber",
      summary: staleWacCount > 99 ? "99+" : String(staleWacCount),
      ariaLabel: `Firefly — open activity panel — ${reasonLabel}`,
      reasonCode: "stale_wac",
      reasonLabel,
    };
  }

  if (isKbAudience && kbGapOpenCount > 0 && ready("kb_gaps")) {
    const reasonLabel =
      kbGapOpenCount === 1 ? "1 knowledge-base gap" : `${kbGapOpenCount} knowledge-base gaps`;
    return {
      tone: "amber",
      summary: kbGapOpenCount > 99 ? "99+" : String(kbGapOpenCount),
      ariaLabel: `Firefly — open activity panel — ${reasonLabel}`,
      reasonCode: "kb_gaps",
      reasonLabel,
    };
  }

  if (isKbAudience && aiPromptingOpenCount > 0 && ready("ai_prompting_suggestions")) {
    const reasonLabel =
      aiPromptingOpenCount === 1
        ? "1 prompting suggestion to review"
        : `${aiPromptingOpenCount} prompting suggestions to review`;
    return {
      tone: "amber",
      summary: aiPromptingOpenCount > 99 ? "99+" : String(aiPromptingOpenCount),
      ariaLabel: `Firefly — open activity panel — ${reasonLabel}`,
      reasonCode: "ai_prompting_suggestions",
      reasonLabel,
    };
  }

  if (isApprovalAudience && pendingMemberApprovalTotal > 0 && ready("pending_approvals")) {
    const n = pendingMemberApprovalTotal;
    const reasonLabel = n === 1 ? "1 member waiting for approval" : `${n} members waiting for approval`;
    return {
      tone: "amber",
      summary: n > 99 ? "99+" : String(n),
      ariaLabel: `Firefly — open activity panel — ${reasonLabel}`,
      reasonCode: "pending_approvals",
      reasonLabel,
    };
  }

  return FALLBACK;
}
